import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const DemoResourcesSection = () => {
  const resources = [
  {
    id: 1,
    type: "guide",
    title: "Cardiovascular Device Policy Guide",
    description: "Comprehensive guide covering Medicare and commercial payer policies for cardiac devices including stents, pacemakers, and monitoring systems.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_195a6e902-1764648555298.png",
    imageAlt: "Medical professional reviewing cardiac device documentation on tablet in modern hospital setting with medical equipment visible in background",
    fileSize: "2.4 MB",
    pages: 45,
    category: "cardiovascular",
    downloadCount: 1247
  },
  {
    id: 2,
    type: "dataset",
    title: "Sample Policy Change Dataset",
    description: "Real-world policy change data from Q4 2025 including Medicare, BCBS, Aetna, and UHC updates across multiple device categories.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e33f658b-1768191173253.png",
    imageAlt: "Business analytics dashboard displaying colorful data visualization charts and graphs on computer screen showing policy trends and statistics",
    fileSize: "856 KB",
    records: 342,
    category: "data",
    downloadCount: 892
  },
  {
    id: 3,
    type: "whitepaper",
    title: "ROI of Automated Policy Monitoring",
    description: "Case study analysis demonstrating cost savings and risk reduction achieved by Fortune 500 medical device manufacturers using PolicyAlerts.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18f69b375-1768339905376.png",
    imageAlt: "Business executive analyzing financial charts and ROI metrics on laptop screen in modern corporate office environment with documents spread on desk",
    fileSize: "1.8 MB",
    pages: 28,
    category: "business",
    downloadCount: 2156
  },
  {
    id: 4,
    type: "guide",
    title: "Orthopedic Implant Policy Tracker",
    description: "Detailed tracking of coverage policies for hip, knee, and spinal implants across major payer networks with quarterly updates.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_105cf29cd-1766810510625.png",
    imageAlt: "Orthopedic surgeon examining X-ray images of hip and knee implants on illuminated medical viewing screen in clinical examination room",
    fileSize: "3.1 MB",
    pages: 52,
    category: "orthopedic",
    downloadCount: 1534
  },
  {
    id: 5,
    type: "video",
    title: "Platform Walkthrough Video",
    description: "15-minute guided tour of PolicyAlerts platform features including alert configuration, policy search, and coverage reporting.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_174e6845c-1769405784329.png",
    imageAlt: "Professional video tutorial screen showing PolicyAlerts software interface with instructor demonstrating platform features on modern computer display",
    duration: "15:32",
    views: 4521,
    category: "tutorial",
    downloadCount: 3287
  },
  {
    id: 6,
    type: "template",
    title: "Policy Monitoring Checklist",
    description: "Customizable checklist template for tracking policy changes based on current Medicare and commercial payer requirements.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11cbf299c-1766564822215.png",
    imageAlt: "Business professional reviewing policy monitoring checklist document with pen in hand at organized desk with laptop and policy binders visible",
    fileSize: "425 KB",
    pages: 12,
    category: "policy",
    downloadCount: 1876
  }];


  const getCategoryIcon = (category) => {
    const icons = {
      cardiovascular: 'Heart',
      orthopedic: 'Bone',
      data: 'Database',
      business: 'TrendingUp',
      tutorial: 'Video',
      policy: 'Shield'
    };
    return icons?.[category] || 'FileText';
  };

  const getCategoryColor = (category) => {
    const colors = {
      cardiovascular: 'text-destructive bg-destructive/10',
      orthopedic: 'text-accent bg-accent/10',
      data: 'text-secondary bg-secondary/10',
      business: 'text-success bg-success/10',
      tutorial: 'text-warning bg-warning/10',
      policy: 'text-primary bg-primary/10'
    };
    return colors?.[category] || 'text-muted-foreground bg-muted';
  };

  return (
    <section className="bg-background py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
            Demo Resources & Downloads
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Access comprehensive guides, sample datasets, and policy resources to explore PolicyAlerts capabilities
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {resources?.map((resource) =>
          <div
            key={resource?.id}
            className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary/20 transition-all duration-300 hover:shadow-lg">

              <div className="relative h-48 overflow-hidden">
                <Image
                src={resource?.image}
                alt={resource?.imageAlt}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />

                <div className="absolute top-3 right-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getCategoryColor(resource?.category)}`}>
                    <Icon name={getCategoryIcon(resource?.category)} size={12} />
                    {resource?.type?.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2 line-clamp-2">
                  {resource?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                  {resource?.description}
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-muted-foreground">
                  {resource?.fileSize &&
                <span className="flex items-center gap-1">
                      <Icon name="HardDrive" size={12} />
                      {resource?.fileSize}
                    </span>
                }
                  {resource?.pages &&
                <span className="flex items-center gap-1">
                      <Icon name="FileText" size={12} />
                      {resource?.pages} pages
                    </span>
                }
                  {resource?.records &&
                <span className="flex items-center gap-1">
                      <Icon name="Database" size={12} />
                      {resource?.records} records
                    </span>
                }
                  {resource?.duration &&
                <span className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      {resource?.duration}
                    </span>
                }
                  {resource?.views &&
                <span className="flex items-center gap-1">
                      <Icon name="Eye" size={12} />
                      {resource?.views?.toLocaleString()} views
                    </span>
                }
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    {resource?.downloadCount?.toLocaleString()} downloads
                  </span>
                  <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left">

                    Download
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 md:mt-12 bg-primary/10 border border-primary/20 rounded-lg p-6 md:p-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Rocket" size={24} color="var(--color-primary)" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">
              Ready for a Personalized Demo?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-6">
              Schedule a live walkthrough with our policy intelligence experts to see how PolicyAlerts can transform your policy monitoring process
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left">

                Schedule Live Demo
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="MessageSquare"
                iconPosition="left">

                Contact Sales Team
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default DemoResourcesSection;